import { ReserveModalProvider } from "@/hooks/useReserveModal";
import { ThemeProvider } from "@gettactic/components/themes/ThemeProvider/ThemeProvider";
import { cn } from "@gettactic/helpers";
import { useMobileAppMessages } from "@gettactic/helpers/src/webview/useMobileAppMessages";
import {
  MS_1_MINUTE,
  useAuthenticated,
  useOrgFeatureFlag,
  useOrganizationSettings
} from "@gettactic/hooks";
import {
  AskTessaContext,
  AskTessaProvider
} from "@v2/components/ai/AskTessaProvider";
import {
  GlobalSearch,
  GlobalSearchProvider,
  useGlobalSearch
} from "@webV2/components/global-search";
import { MapModalProvider } from "@webV2/components/map-modal";
import { MainSidebar } from "@webV2/components/sidebars/MainSidebar/MainSidebar";
import { applyThemeColors, applyThemeFont } from "@webV2/layouts/theme";
import { AskTessa } from "@webV2/pages/Tessa/AskTessa";
import { ImpersonateProvider } from "@webV2/providers/ImpersonateProvider";
import { LayoutProvider } from "@webV2/providers/LayoutProvider/LayoutProvider";
import { ReserveProvider } from "@webV2/providers/ReserveProvider/ReserveProvider";
import { useRouter } from "next/router";
import React from "react";

const classes = {
  Wrapper: "app-main-layout flex h-screen w-full",
  Sidebar:
    "app-main-layout-sidebar md:border-r md:border-r-neutral-300 bg-v2-sidebar text-v2-sidebar-foreground",
  MainContent: "app-main-layout-content flex-1"
};

export const MainLayoutInner: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const router = useRouter();
  useMobileAppMessages((message) => {
    switch (message.action) {
      case "navigate":
        switch (message.screen) {
          case "user-settings":
            router.push("/settings/profile");
            break;
        }
        break;

      case "openUrl":
        window.open(message.url, "_blank");
        break;
    }
  });
  return <div className={classes.MainContent}>{children}</div>;
};

export const AskTessaFeatureFlagContainer: React.FC<
  React.PropsWithChildren<{
    isEnabled?: boolean;
  }>
> = ({ isEnabled, children }) => {
  if (!isEnabled) return children;

  return <AskTessaProvider>{children}</AskTessaProvider>;
};

const GlobalSearchWrapper: React.FC = () => {
  const { isOpen, close } = useGlobalSearch();
  return <GlobalSearch isOpen={isOpen} onClose={close} />;
};

export const AppMainLayout: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const tessaFeatureFlag = useOrgFeatureFlag("tessa");
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const organizationId = authenticatedUser?.organization?.id;

  const { data: organizationSettings } = useOrganizationSettings({
    id: organizationId,
    refetchInterval: MS_1_MINUTE
  });

  React.useEffect(() => {
    if (organizationSettings?.theme_settings?.colors) {
      applyThemeColors(organizationSettings.theme_settings.colors);
    }

    if (organizationSettings?.theme_settings?.font) {
      applyThemeFont(organizationSettings.theme_settings.font);
    }
  }, [organizationSettings]);

  return (
    <ThemeProvider
      defaultBrand="base"
      storageKey="ui-theme-new"
      externalBrand="base"
    >
      <GlobalSearchProvider>
        <LayoutProvider>
          <ReserveProvider>
            <ReserveModalProvider>
              <MapModalProvider>
                <ImpersonateProvider>
                  <AskTessaFeatureFlagContainer
                    isEnabled={tessaFeatureFlag || false}
                  >
                    <AskTessaContext.Consumer>
                      {(askTessa) => {
                        const isChatDocked =
                          askTessa?.tessaDisclosure?.isOpen &&
                          !!askTessa?.dockedSide &&
                          askTessa?.isPopup.state;

                        return (
                          <div
                            className={cn(
                              "transition-[margin]",
                              isChatDocked &&
                                "relative isolate flex h-screen flex-col",
                              isChatDocked &&
                                askTessa?.dockedSide === "left" &&
                                "ml-[415px]",
                              isChatDocked &&
                                askTessa?.dockedSide === "right" &&
                                "mr-[415px]"
                            )}
                          >
                            {tessaFeatureFlag && <AskTessa />}
                            <div className={classes.Wrapper}>
                              <div className={classes.Sidebar}>
                                <MainSidebar />
                              </div>
                              <MainLayoutInner>{children}</MainLayoutInner>
                            </div>
                            <GlobalSearchWrapper />
                          </div>
                        );
                      }}
                    </AskTessaContext.Consumer>
                  </AskTessaFeatureFlagContainer>
                </ImpersonateProvider>
              </MapModalProvider>
            </ReserveModalProvider>
          </ReserveProvider>
        </LayoutProvider>
      </GlobalSearchProvider>
    </ThemeProvider>
  );
};
